// "The server is busy" while any request waits out a 429 (J-210, inbox 06 PR E point 11). Several
// requests can wait at once; the signal stays on until the last one is sent again. The layout reads it
// through `useServerBusy` and shows one status line.
type Listener = () => void;

export interface BusyState {
  /** Requests waiting out a 429. */
  waiting: number;
  /** The longest wait announced, in seconds. */
  seconds: number;
}

const IDLE: BusyState = { waiting: 0, seconds: 0 };

export function createBusySignal() {
  let state: BusyState = IDLE;
  // Which requests are waiting, so `end` for a request that never waited changes nothing.
  let open = 0;
  const listeners = new Set<Listener>();
  const set = (next: BusyState) => {
    state = next;
    for (const listener of listeners) listener();
  };
  return {
    /** A request starts waiting `seconds`; returns the function that ends its wait, once. */
    start(seconds: number): () => void {
      open += 1;
      set({ waiting: open, seconds: Math.max(state.seconds, seconds) });
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        open -= 1;
        set(open === 0 ? IDLE : { ...state, waiting: open });
      };
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    snapshot: (): BusyState => state,
  };
}

export const busySignal = createBusySignal();
